import { requireSupabase, supabase } from "@/lib/supabaseClient";
import { guard } from "@/lib/errors";

// Embedded selects use the default Postgres FK constraint names created by the
// schema (messages_sender_id_fkey / messages_recipient_id_fkey). If embedding
// is unavailable for any reason, the fields resolve to null — callers degrade
// gracefully.
const ENRICHED_SELECT =
  "*, sender:profiles!messages_sender_id_fkey(id,full_name,email,role), recipient:profiles!messages_recipient_id_fkey(id,full_name,email,role), property:properties(id,title)";

const convKey = (otherId, propertyId) => `${otherId}::${propertyId || "general"}`;

export const messageService = {
  // ---- Existing API (unchanged, backward compatible) -----------------------
  inbox: (userId) =>
    guard("messageService.inbox", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    }),

  send: ({ senderId, recipientId, propertyId = null, body }) =>
    guard("messageService.send", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("messages")
        .insert({ sender_id: senderId, recipient_id: recipientId, property_id: propertyId, body })
        .select(ENRICHED_SELECT)
        .maybeSingle();
      if (error) throw error;
      return data;
    }),

  markRead: (id) =>
    guard("messageService.markRead", async () => {
      const supabase = requireSupabase();
      const { error } = await supabase.from("messages").update({ status: "read" }).eq("id", id);
      if (error) throw error;
      return true;
    }),

  // ---- New API (Chunk 2 — Internal Messaging) ------------------------------

  /**
   * Returns every message that involves the user, enriched with the other
   * party's profile and (when available) the related property.
   */
  all: (userId) =>
    guard("messageService.all", async () => {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("messages")
        .select(ENRICHED_SELECT)
        .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data || [];
    }),

  /**
   * Groups the user's messages into conversations keyed by counterpart +
   * property. Each conversation returns the counterpart, related property, the
   * last message, an unread count (inbound + not read) and the full thread.
   */
  conversations: (userId) =>
    guard("messageService.conversations", async () => {
      const rows = await messageService.all(userId);
      const map = new Map();
      for (const m of rows) {
        const otherIsSender = m.sender_id !== userId;
        const otherId = otherIsSender ? m.sender_id : m.recipient_id;
        const otherProfile = otherIsSender ? m.sender : m.recipient;
        const key = convKey(otherId, m.property_id);
        if (!map.has(key)) {
          map.set(key, {
            key,
            otherId,
            other: otherProfile || null,
            propertyId: m.property_id || null,
            property: m.property || null,
            messages: [],
            unread: 0,
            last: null,
          });
        }
        const conv = map.get(key);
        conv.messages.push(m);
        conv.last = m;
        if (!conv.other && otherProfile) conv.other = otherProfile;
        if (!conv.property && m.property) conv.property = m.property;
        if (m.recipient_id === userId && m.status !== "read") conv.unread += 1;
      }
      return Array.from(map.values()).sort(
        (a, b) => new Date(b.last?.created_at || 0) - new Date(a.last?.created_at || 0)
      );
    }),

  /** Total count of inbound messages that have not yet been read. */
  unreadCount: (userId) =>
    guard("messageService.unreadCount", async () => {
      const supabase = requireSupabase();
      const { count, error } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("recipient_id", userId)
        .neq("status", "read");
      if (error) throw error;
      return count || 0;
    }),

  /**
   * Marks every inbound message in a conversation as read (read receipts).
   * Only the recipient's own messages are updated, which RLS permits.
   */
  markThreadRead: (userId, otherId, propertyId = null) =>
    guard("messageService.markThreadRead", async () => {
      const supabase = requireSupabase();
      let q = supabase
        .from("messages")
        .update({ status: "read" })
        .eq("recipient_id", userId)
        .eq("sender_id", otherId)
        .neq("status", "read");
      q = propertyId ? q.eq("property_id", propertyId) : q.is("property_id", null);
      const { error } = await q;
      if (error) throw error;
      return true;
    }),

  /**
   * Subscribes to realtime message inserts/updates that involve the user.
   * Returns an unsubscribe function. Safe no-op when Supabase is unavailable
   * or realtime is not enabled on the project.
   */
  subscribe: (userId, onChange) => {
    if (!supabase || !userId) return () => {};
    try {
      const channel = supabase
        .channel(`messages:${userId}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "messages", filter: `recipient_id=eq.${userId}` },
          (payload) => onChange?.(payload)
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "messages", filter: `sender_id=eq.${userId}` },
          (payload) => onChange?.(payload)
        )
        .subscribe();
      return () => {
        try {
          supabase.removeChannel(channel);
        } catch {
          /* ignore */
        }
      };
    } catch (err) {
      console.debug("[messageService] realtime subscription unavailable:", err?.message || err);
      return () => {};
    }
  },
};
